import type {
  CandidatePipelineStep,
  CandidatePipelineStepId,
  JobApplication,
  JobApplicationStatus,
  JobOfferClauses,
  JobOfferFields,
  JobOfferLetter,
  JobOfferSection,
} from '../types/hiring';

export const LCTF_OFFER_COMPANY = "Leiper's Creek Timber Frames";
export const LCTF_OFFER_LEGAL = "MJL Enterprises, LLC dba Leiper's Creek Timber Frames";
export const LCTF_DEFAULT_CEO_NAME = 'Matt Lynch';
export const LCTF_DEFAULT_CEO_TITLE = 'CEO';

const SHOP_TITLE_RE = /framer|carpenter|shop|timber|install|raiser|crew|yard/i;
const EXEC_TITLE_RE = /coo|ceo|chief|officer|director|vice president|\bvp\b|executive/i;
const SALES_TITLE_RE = /\bsales\b/i;

export const SALES_CONFIDENTIAL_ACCESS =
  'This position includes access to confidential and proprietary Company data and pricing.';

export function isSalesOfferTitle(title: string): boolean {
  return SALES_TITLE_RE.test(title || '');
}

export function employeeFullName(fields: Pick<JobOfferFields, 'employeeFirstName' | 'employeeLastName'>): string {
  return `${fields.employeeFirstName || ''} ${fields.employeeLastName || ''}`.replace(/\s+/g, ' ').trim();
}

export function normalizePersonName(name: string): string {
  return name.replace(/\s+/g, ' ').trim().toLowerCase();
}

export function namesMatchApplicant(
  typed: string,
  person: Pick<JobOfferFields, 'employeeFirstName' | 'employeeLastName'> | Pick<JobApplication, 'firstName' | 'lastName'>,
): boolean {
  const fields = 'employeeFirstName' in person
    ? person
    : { employeeFirstName: person.firstName, employeeLastName: person.lastName };
  return normalizePersonName(typed) === normalizePersonName(employeeFullName(fields));
}

export function formatOfferPay(fields: Pick<JobOfferFields, 'payType' | 'payAmount' | 'hoursPerWeek'>): string {
  const amount = Number(fields.payAmount) || 0;
  const money = amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: amount % 1 ? 2 : 0 });
  if (fields.payType === 'hourly') {
    return `${money} per hour`;
  }
  const hours = fields.hoursPerWeek || 40;
  const hourly = hours > 0 ? amount / (hours * 52) : 0;
  const hourlyLabel = hourly.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return `${money} per year (about ${hourlyLabel}/hour at ${hours} hours per week)`;
}

export function formatOfferDate(iso: string): string {
  if (!iso) return 'the date agreed with the Company';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function formatOfferMoney(amount: number): string {
  const n = Number(amount) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: n % 1 ? 2 : 0 });
}

function dayOrdinal(day: number): string {
  const v = day % 100;
  if (v >= 11 && v <= 13) return `${day}th`;
  if (day % 10 === 1) return `${day}st`;
  if (day % 10 === 2) return `${day}nd`;
  if (day % 10 === 3) return `${day}rd`;
  return `${day}th`;
}

export function formatOfferDateCommencing(iso: string): string {
  if (!iso) return 'the start date agreed with the Company';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `the ${dayOrdinal(d.getDate())} day of ${d.toLocaleDateString('en-US', { month: 'long' })} ${d.getFullYear()}`;
}

export function defaultOfferDuties(positionTitle: string): string {
  if (EXEC_TITLE_RE.test(positionTitle)) {
    return 'Following an initial training period, Employee will serve as a primary leader for project fulfillment: coordinating installations, materials, equipment, schedules, customers, designers, and engineers, and upholding safety and quality standards.';
  }
  return 'Employee’s primary responsibilities will consist of on-site construction, timber framing, installation, material handling, and other manual labor and construction-related duties as assigned by the Company.';
}

export function defaultOfferClauses(positionTitle: string, payType: JobOfferFields['payType']): JobOfferClauses {
  const shop = SHOP_TITLE_RE.test(positionTitle);
  const exec = EXEC_TITLE_RE.test(positionTitle);
  const sales = isSalesOfferTitle(positionTitle);
  return {
    travelInstall: shop || exec,
    salaryNoOvertime: payType === 'salary' && (shop || exec),
    hourlyOvertime: payType === 'hourly',
    profitSharePercent: null,
    sepIraPercent: null,
    healthReimbursementMonthly: null,
    salesCommission: sales,
    salesCommissionPercent: sales ? 1 : null,
    executiveResponsibilities: exec,
    executiveResponsibilitiesText: exec
      ? [
        'Ordering timbers and materials.',
        'Renting necessary machinery and equipment.',
        'Scheduling installations and project timelines.',
        'Coordinating with customers to ensure smooth communication and satisfaction.',
        'Collaborating with designers and engineers on project specifications.',
        'Overseeing construction after sale, including safety, quality, and efficient execution.',
        'Managing customer projects in the company CRM.',
      ].join('\n')
      : '',
  };
}

const DESIGNATION_TITLES: Record<string, string> = {
  project_manager: 'Project Manager',
  estimator: 'Estimator',
  executive_assistant: 'Executive Assistant',
  sales_rep: 'Sales Representative',
  cfo: 'Chief Financial Officer',
  coo: 'Chief Operating Officer',
  office_manager: 'Office Manager',
};

export function offerReportsToPosition(person: { title?: string; designations?: string[] }): string {
  const title = (person.title || '').trim();
  if (title) return title;
  const designation = (person.designations || []).find(d => DESIGNATION_TITLES[d]);
  return designation ? DESIGNATION_TITLES[designation] : '';
}

export function offerReportsToClause(position: string): string {
  const title = position.trim();
  if (!title) return '';
  const withArticle = /^(the|a|an)\s/i.test(title) ? title : `the ${title}`;
  return `This role reports to ${withArticle}.`;
}

export function defaultOfferFields(application: Pick<JobApplication, 'firstName' | 'lastName' | 'email' | 'jobTitle'>): JobOfferFields {
  const positionTitle = application.jobTitle || 'Team Member';
  return {
    employeeFirstName: application.firstName,
    employeeLastName: application.lastName,
    employeeEmail: application.email || '',
    positionTitle,
    duties: defaultOfferDuties(positionTitle),
    startDate: '',
    payType: 'salary',
    payAmount: 0,
    hoursPerWeek: 40,
    employmentType: 'full-time',
    location: 'Franklin, Tennessee shop, with travel to project sites as required',
    reportingTo: '',
    reportingToUserId: '',
    responseDeadline: '',
    ceoName: LCTF_DEFAULT_CEO_NAME,
    ceoTitle: LCTF_DEFAULT_CEO_TITLE,
  };
}

export const LCTF_OFFER_ADDRESS = [
  '1201 Liberty Pike #229',
  'Franklin, TN 37067',
  'info@lctimberframes.com',
  '615-716-8490',
].join('\n');

function salesCommissionPercent(clauses: JobOfferClauses): number | null {
  if (!clauses.salesCommission) return null;
  const pct = clauses.salesCommissionPercent == null ? 1 : clauses.salesCommissionPercent;
  return pct > 0 ? pct : null;
}

function formatClausePercent(pct: number): string {
  return Number.isInteger(pct) ? String(pct) : String(pct);
}

/** Part-time offers name the assumed weekly hours instead of a "standard" workweek. */
function compensationParagraph(fields: JobOfferFields): string {
  const hours = fields.hoursPerWeek || 40;
  const money = formatOfferMoney(fields.payAmount);
  const partTime = fields.employmentType === 'part-time';
  const hourlyEquiv = hours > 0 && fields.payType === 'salary' && fields.payAmount
    ? formatOfferMoney(fields.payAmount / (hours * 52))
    : '';
  if (fields.payType === 'hourly') {
    if (partTime) {
      return `Your pay will be ${money} per hour. This position is part-time, and we have based this offer on ${hours} hours per week, paid through the Company’s regular payroll.`;
    }
    return `Your pay will be ${money} per hour, based on a standard ${hours}-hour workweek, paid through the Company’s regular payroll.`;
  }
  if (partTime) {
    return hourlyEquiv
      ? `Your annual base salary will be ${money}. This position is part-time, and we have based this offer on ${hours} hours per week, which equates to approximately ${hourlyEquiv} per hour.`
      : `Your annual base salary will be ${money}. This position is part-time, and we have based this offer on ${hours} hours per week, paid through the Company’s regular payroll.`;
  }
  return hourlyEquiv
    ? `Your annual base salary will be ${money}, which equates to approximately ${hourlyEquiv} per hour based on a standard ${hours}-hour workweek.`
    : `Your annual base salary will be ${money}, paid through the Company’s regular payroll.`;
}

function agreementPayClause(fields: JobOfferFields): string {
  const title = fields.positionTitle || 'Team Member';
  const money = formatOfferMoney(fields.payAmount);
  const base = fields.payType === 'hourly'
    ? `In exchange for assuming the duties of ${title}, Employee will be paid ${money} per hour.`
    : `In exchange for assuming the duties of ${title}, Employee will be paid ${money}, per year.`;
  if (fields.employmentType !== 'part-time') return base;
  const hours = fields.hoursPerWeek || 40;
  return `${base} This position is part-time, and this offer is based on ${hours} hours per week.`;
}

function withSalesConfidentialAccess(text: string, title: string): string {
  if (!isSalesOfferTitle(title)) return text;
  if (/confidential and proprietary/i.test(text)) return text;
  return [text, SALES_CONFIDENTIAL_ACCESS].filter(Boolean).join('\n\n');
}

function employmentTypeLabel(type: JobOfferFields['employmentType']): string {
  if (type === 'full-time') return 'Full-time';
  if (type === 'part-time') return 'Part-time';
  if (type === 'seasonal') return 'Seasonal';
  return 'Contract';
}

function responsibilityLines(text: string): string {
  return text
    .split('\n')
    .map(l => l.replace(/^[•\-\d.]+\s*/, '').trim())
    .filter(Boolean)
    .map(l => `• ${l}`)
    .join('\n');
}

function travelInstallBody(): string {
  return [
    'This position includes travel for on-site timber frame installations and related work at job sites around the country, as assigned by the Company.',
    'Because this position includes travel for on-site installs around the country, the following additional terms apply to travel and installation assignments:',
    '(a) No overtime on travel or installation jobs. Travel and on-site installation work may require days of up to 12 hours. Such hours are covered by Employee’s regular compensation as described above unless applicable law requires otherwise.',
    '(b) Weekend travel days. When the Company requires Employee to travel on a Saturday or Sunday to or from a job site, that weekend travel day will be paid as a regular workday (it will count as a paid workday), though work will not usually be expected on weekends.',
    '(c) Per diem. On overnight travel assignments away from Employee’s home area, Employee will be eligible for a reimbursable per diem for meals and incidental expenses at the rate then established by the Company, subject to the Company’s expense and documentation policies. Per diem is a reimbursement of business expense and is not additional wages unless required by applicable law.',
    '(d) Other travel expenses. Reasonable, pre-approved travel expenses (including lodging arranged or approved by the Company, and necessary transportation) will be paid or reimbursed in accordance with Company policy. Employee must submit receipts and documentation as required by the Company.',
    '(e) Assignment. The Company will provide reasonable notice of travel assignments when practicable. Employee agrees to travel as reasonably required for the position.',
  ].join('\n\n');
}

function travelAndOvertimeSections(fields: JobOfferFields, clauses: JobOfferClauses): JobOfferSection[] {
  const sections: JobOfferSection[] = [];
  const overtime: string[] = [];
  if (clauses.salaryNoOvertime && fields.payType === 'salary') {
    overtime.push('The salary described in this Section is intended to compensate Employee for all hours worked in connection with the position. There are no overtime provisions under this Agreement. Employee will not receive additional overtime pay for hours worked in excess of 40 hours in a workweek.');
  }
  if (clauses.hourlyOvertime && fields.payType === 'hourly') {
    overtime.push('Because this is an hourly position, hours worked in excess of 40 in a workweek will be paid as overtime in accordance with applicable law, unless a lawful exemption applies.');
  }
  if (overtime.length) {
    sections.push({
      id: 'ea-overtime',
      heading: '2.4 Hours of Work',
      body: overtime.join('\n\n'),
    });
  }
  if (clauses.travelInstall) {
    sections.push({
      id: 'ea-travel',
      heading: '2.5 Travel Assignments and Related Pay Practices',
      body: travelInstallBody(),
    });
  }
  return sections;
}

export function assembleJobOfferSections(fields: JobOfferFields, clauses: JobOfferClauses): JobOfferSection[] {
  const name = employeeFullName(fields) || 'Employee';
  const first = (fields.employeeFirstName || '').trim() || name;
  const start = formatOfferDate(fields.startDate);
  const location = fields.location.trim()
    || (clauses.travelInstall
      ? 'Franklin, Tennessee shop, with travel to project sites as required'
      : 'Franklin, Tennessee shop');

  const details = [
    `• Title: ${fields.positionTitle || 'Team Member'}`,
    `• Start Date: ${fields.startDate ? start : 'to be confirmed'}`,
    fields.reportingTo ? `• Reporting To: ${fields.reportingTo}` : '',
    `• Employment Type: ${employmentTypeLabel(fields.employmentType)}`,
    `• Location: ${location}`,
  ].filter(Boolean).join('\n');

  const payParas: string[] = [compensationParagraph(fields)];
  const commissionPct = salesCommissionPercent(clauses);
  if (commissionPct) {
    payParas.push(`You will receive a sales commission of ${formatClausePercent(commissionPct)}% of the purchase price for each timber frame you sell. Questions about what qualifies as ownership for this commission are answered in the Lead Ownership policy.`);
  }
  if (clauses.profitSharePercent && clauses.profitSharePercent > 0) {
    payParas.push(`In addition, you will be eligible for a ${clauses.profitSharePercent}% profit sharing bonus, calculated annually based on the company’s net profits and disbursed at the end of each fiscal year, subject to your continued employment and performance.`);
  }

  const benefitLines: string[] = [];
  if (clauses.sepIraPercent && clauses.sepIraPercent > 0) {
    benefitLines.push(`Retirement Contribution: A ${clauses.sepIraPercent}% of salary company contribution to a Simplified Employee Pension (SEP) IRA, with the potential for upward adjustments at the CEO’s discretion.`);
  }
  if (clauses.healthReimbursementMonthly && clauses.healthReimbursementMonthly > 0) {
    benefitLines.push(`Health Insurance Reimbursement: A monthly reimbursement of ${formatOfferMoney(clauses.healthReimbursementMonthly)} toward your health insurance premiums.`);
  }
  const benefits = [
    `${LCTF_OFFER_COMPANY} offers a benefits package to support your well-being and financial security.`,
    benefitLines.length
      ? benefitLines.map((line, i) => `${i + 1}. ${line}`).join('\n')
      : '',
    'Additional benefits may include paid time off, holidays, and other perks as outlined in our employee handbook, which will be provided upon your start date. Benefits eligibility begins after 60 days of employment.',
  ].filter(Boolean).join('\n\n');

  const execLines = clauses.executiveResponsibilities && clauses.executiveResponsibilitiesText.trim()
    ? responsibilityLines(clauses.executiveResponsibilitiesText)
    : '';
  const duties = withSalesConfidentialAccess(fields.duties.trim(), fields.positionTitle);
  const responsibilities = [
    duties,
    execLines ? `You will also be responsible for:\n\n${execLines}` : '',
    'This position requires a commitment to upholding the highest standards of safety, professionalism, and operational excellence.',
  ].filter(Boolean).join('\n\n');

  const acceptBy = fields.responseDeadline
    ? ` If you accept this offer, please sign below by ${formatOfferDate(fields.responseDeadline)}.`
    : ' If you accept this offer, please sign below.';

  return [
    {
      id: 'letterhead',
      heading: LCTF_OFFER_COMPANY,
      body: LCTF_OFFER_ADDRESS,
    },
    {
      id: 'offer-intro',
      heading: 'Offer of Employment',
      body: [
        `Dear ${first},`,
        `We are pleased to extend this formal offer of employment for the position of ${fields.positionTitle || 'Team Member'} at ${LCTF_OFFER_COMPANY}. We believe your skills and experience will be a valuable addition to our team.`,
      ].join('\n\n'),
    },
    {
      id: 'position',
      heading: 'Position Details',
      body: details,
    },
    {
      id: 'compensation',
      heading: 'Compensation',
      body: payParas.join('\n\n'),
    },
    {
      id: 'benefits',
      heading: 'Benefits',
      body: benefits,
    },
    {
      id: 'responsibilities',
      heading: 'Job Expectations and Responsibilities',
      body: responsibilities,
    },
    ...(clauses.travelInstall
      ? [{
        id: 'travel',
        heading: 'Travel and Installation',
        body: travelInstallBody(),
      }]
      : []),
    {
      id: 'conditions',
      heading: 'Conditions',
      body: [
        `This offer is contingent upon the successful completion of any required background checks, reference verifications, and proof of eligibility to work in the United States. Employment with ${LCTF_OFFER_COMPANY} is at-will, meaning either you or the company may terminate the employment relationship at any time, with or without cause or notice.`,
        `After you accept this offer, you will review and sign a separate Employment Agreement.${acceptBy} We look forward to welcoming you to the team.`,
      ].join('\n\n'),
    },
    {
      id: 'close',
      heading: 'Sincerely',
      body: `${fields.ceoName || LCTF_DEFAULT_CEO_NAME}\n${fields.ceoTitle || LCTF_DEFAULT_CEO_TITLE}\n${LCTF_OFFER_COMPANY}`,
    },
    {
      id: 'ack',
      heading: 'Acceptance',
      body: `I accept the offer of employment as outlined above.\n\nPrinted Name: ${name}`,
    },
  ];
}

export function assembleEmploymentAgreementSections(fields: JobOfferFields, clauses: JobOfferClauses): JobOfferSection[] {
  const name = employeeFullName(fields) || 'Employee';
  const nameCaps = name.toUpperCase();
  const title = fields.positionTitle || 'Team Member';
  const effective = formatOfferDate(fields.startDate);
  const commencing = formatOfferDateCommencing(fields.startDate);
  const payClause = agreementPayClause(fields);
  const bonusPct = clauses.profitSharePercent && clauses.profitSharePercent > 0 ? clauses.profitSharePercent : null;
  const commissionPct = salesCommissionPercent(clauses);
  const compClauses: string[] = [`2.1 ${fields.payType === 'hourly' ? 'Wages' : 'Salary'}. ${payClause}`];
  let compNumber = 2;
  if (commissionPct) {
    compClauses.push(`2.${compNumber} Sales Commission. Employee will receive a commission of ${formatClausePercent(commissionPct)}% of the purchase price of each timber frame sold by Employee. Questions about what qualifies as ownership for this commission are answered in the Company’s Lead Ownership policy.`);
    compNumber += 1;
  }
  if (bonusPct) {
    compClauses.push(`2.${compNumber} Bonus. Employee will receive a bonus of ${bonusPct}% (the “Bonus”), as of the Effective Date. The Bonus will be calculated as ${bonusPct}% of the Company’s net profit, calculated on a cash basis. The Bonus will be paid out at the end of each fiscal year, subject to continued employment and performance.`);
    compNumber += 1;
  }
  compClauses.push(`2.${compNumber} During Employee’s employment with the Company, Employee may be eligible for additional bonuses awarded at the Company’s discretion.`);

  return [
    {
      id: 'ea-preamble',
      heading: 'Employment Agreement',
      body: [
        `THIS EMPLOYMENT AGREEMENT (“Agreement”) is made as of ${effective} (the “Effective Date”), by and between ${LCTF_OFFER_LEGAL}, a Tennessee limited liability company (the “Company”), and ${nameCaps} (the “Employee”).`,
        'RECITALS:',
        `WHEREAS, by means of this Agreement, the Company and Employee wish to enter into an agreement pursuant to which Employee will work as ${title} for the Company; and`,
        'NOW, THEREFORE, in consideration of the mutual promises, representations, warranties, covenants and conditions hereinafter set forth, the parties hereto agree as follows:',
      ].join('\n\n'),
    },
    {
      id: 'ea-term',
      heading: '1. Term of Employment',
      body: [
        withSalesConfidentialAccess(
          `Company agrees to employ Employee as ${title}, and the Employee hereby accepts such employment (“Employment”) commencing on ${commencing}, and continuing for an indefinite term, unless otherwise terminated as provided herein.`,
          title,
        ),
        'Employee’s employment with the Company shall be at will. Nothing in this Agreement interferes with or limits the Company’s or the Employee’s right to terminate employment at any time, for any reason or no reason, with or without notice.',
      ].join('\n\n'),
    },
    {
      id: 'ea-comp',
      heading: '2. Compensation and Benefits',
      body: [
        'Employee will be compensated during the Employment as follows:',
        ...compClauses,
      ].join('\n\n'),
    },
    ...travelAndOvertimeSections(fields, clauses),
    {
      id: 'ea-termination',
      heading: '3. Termination',
      body: [
        'Employee acknowledges and agrees that nothing in this Agreement is a guarantee or assurance of employment for any specific period of time.',
        'Employee understands that Employee is an at-will employee and that either Employee or the Company may terminate this at-will employment relationship at any time for any reason not prohibited by law.',
      ].join('\n\n'),
    },
    {
      id: 'ea-dissolution',
      heading: '4. Termination Upon Dissolution',
      body: 'The Company shall have the right to terminate this Agreement in the event of the dissolution of the Company.',
    },
    {
      id: 'ea-tax',
      heading: '5. Tax Matters',
      body: [
        'It is the intention of the parties that this Agreement comply with Internal Revenue Code Section 409A and the Treasury Regulations promulgated thereunder (“Section 409A”), and the parties have therefore drafted this Agreement in good faith compliance with Section 409A. To the extent that there is any inconsistency between Section 409A and this Agreement, the parties agree that this Agreement shall be deemed retroactively amended in a manner that most closely maintains the original intent of the parties, to the extent possible, without violating the requirements of or otherwise causing accelerated income inclusion or the imposition of penalties under Section 409A.',
        'The Employee acknowledges that the Company has not provided the Employee with tax advice regarding the awards hereunder, and has urged the Employee to consult with the Employee’s own tax advisor with respect to the income tax consequences of such awards.',
      ].join('\n\n'),
    },
    {
      id: 'ea-noncompete',
      heading: '6. Non-Competition and Non-Solicitation',
      body: [
        `6.1 Non-Competition. Employee agrees that during Employee’s employment, Employee will not engage in any other business activity which is in conflict or competes with Employee’s duties and obligations at ${LCTF_OFFER_COMPANY}. Employee agrees that during the Non-Compete Period (as defined below) Employee shall not, within any county in which ${LCTF_OFFER_COMPANY} does business or did business at any time during the one (1) year period prior to the termination of Employee’s employment with ${LCTF_OFFER_COMPANY}, directly or indirectly engage in or participate in any activity relating to the development, manufacture, production, publication, sale, or distribution of any products or services which would compete with any products or services which are (or are planned to be) developed, manufactured, produced, published, sold, or distributed by ${LCTF_OFFER_COMPANY} or any of its affiliates, either as an officer, employee, partner, owner, manager, operator, associate, or agent of or consultant. The Non-Compete Period shall cover the entire period of Employee’s employment with Employer and two (2) years after Employee’s employment with Employer terminates for any reason.`,
        '6.2 Non-Solicitation. Employee further agrees during Employee’s employment, and for a period of two (2) years after the termination of Employee’s employment with the Company not to directly or indirectly:',
        '(a) solicit the services of or hire or form a business association with any of the Company’s employees, independent contractors, or otherwise induce or attempt to induce any such individual or entity who had a business relationship with the Company during Employee’s employment or at any time during the one (1) year period prior to the termination of Employee’s employment with the Company, to terminate or alter his, her or its employment or business relationship with the Company;',
        '(b) influence or attempt to influence any active or prospective client of the Company to terminate or modify any written or oral agreement or course of dealing with the Company;',
        '(c) on behalf of any competing entity (as described in paragraph 6.2(a) above) influence or attempt to influence any vendor or supplier of the Company during Employee’s employment with the Company, to terminate or modify any written or oral agreement or course of dealing with the Company.',
        '6.3 Reasonableness. The parties agree that the time span, scope, and geographic area covered by this section are reasonable and necessary to protect Employer from competing efforts. If, however, it shall be judicially determined that any provision of this Section is unreasonable in one or more respects, the provision shall not be declared invalid but rather shall be modified to the extent that it shall be determined to be reasonable. The existence of any claim or cause of action by Employee against Employer, whether predicated upon this agreement or otherwise, shall not constitute a defense to the enforcement by Employer of the provisions in this Section.',
        '6.4 Remedies. Employee acknowledges that a breach of this Section would result in irreparable damage to Employer, and without limiting other remedies which may exist for a breach of this Section, Employee agrees that this Section may be enforced by temporary restraining order, temporary injunction, and permanent injunction restraining a violation. Employer shall be entitled to all costs, including reasonable attorneys’ fees, associated with any effort to enforce this Section.',
        '6.5 Separate Obligation. Employee acknowledges that the obligations set forth in this section are separate and apart from other provisions of this Agreement and that a breach of this Agreement is not a defense to enforceability of the Non-Competition and Non-Solicitation provisions stated herein.',
      ].join('\n\n'),
    },
    {
      id: 'ea-binding',
      heading: '7. Binding Agreement',
      body: 'This Agreement and the representations and warranties contained herein shall be binding upon the heirs, personal representative, successors and assigns of the Employee.',
    },
    {
      id: 'ea-law',
      heading: '8. Governing Law',
      body: 'This Agreement shall be governed by and construed in accordance with the internal laws of the State of Tennessee.',
    },
    {
      id: 'ea-beneficiaries',
      heading: '9. Beneficiaries',
      body: 'This Agreement is for the benefit of the parties hereto, and no third party is intended to be a beneficiary hereof.',
    },
    {
      id: 'ea-indemnity',
      heading: '10. Indemnification',
      body: 'The Employee agrees to indemnify and hold harmless the Company and its members, managers, officers and employees against all losses, liabilities, claims, damages and expenses whatsoever (including, but not limited to, all expenses reasonably incurred in investigating, preparing or defending against any litigation commenced or threatened or any claim whatsoever) arising out of or based upon any false representation or warranty or breach or failure by the Employee to comply with any covenant or agreement made by the Employee herein or in any other document furnished by the Employee to the Company in connection with the transactions contemplated hereby.',
    },
    {
      id: 'ea-assignment',
      heading: '11. Assignment',
      body: 'The Employee acknowledges that the benefits to be afforded hereunder are personal and specific to the Employee, and therefore agrees that neither this Agreement nor any rights which may accrue to the Employee hereunder may be transferred or assigned without the prior written consent of the Company.',
    },
    {
      id: 'ea-entire',
      heading: '12. Entire Agreement',
      body: 'This Agreement contains the entire agreement between the parties with respect to the subject matter hereof, and this Agreement may be amended or superseded only by a writing executed by the parties.',
    },
    {
      id: 'ea-amendment',
      heading: '13. Amendment',
      body: 'Any modification of this Agreement or additional obligation assumed by either party in connection with this Agreement shall be binding only if evidenced in writing signed by each party or an authorized representative of each party.',
    },
    {
      id: 'ea-severability',
      heading: '14. Severability',
      body: 'If any provision, or any portion thereof, contained in this Agreement is held to be unconstitutional, invalid, or unenforceable, the remainder of this Agreement, or portion thereof, shall be deemed severable, shall not be affected, and shall remain in full force and effect.',
    },
    {
      id: 'ea-counterparts',
      heading: '15. Counterparts; Delivery',
      body: 'This Agreement may be executed and acknowledged in counterparts, each of which shall be deemed an original, but all of which together shall constitute one and the same instrument. Facsimile transmission (including the e-mail delivery of documents in Adobe PDF format) of any signed original counterpart and/or retransmission of any signed facsimile transmission shall be deemed the same as the delivery of an original.',
    },
    {
      id: 'ea-witness',
      heading: 'IN WITNESS WHEREOF',
      body: `Intending to be legally bound hereby, the Company and the Employee have duly executed this Agreement as of the day and year first above written.`,
    },
  ];
}

export function offerLetterIsSigned(offer: Pick<JobOfferLetter, 'employeeSignature'> | null | undefined): boolean {
  return Boolean(offer?.employeeSignature?.dataUrl);
}

export type HiredSeat = 'employee' | 'contractor';

/** W-2 employment agreements stay employees. A contract engagement is a 1099. */
export function hiredSeatForOffer(offer: Pick<JobOfferLetter, 'fields'>): HiredSeat {
  return offer.fields.employmentType === 'contract' ? 'contractor' : 'employee';
}

/**
 * Signed the employment agreement and finished every step after it
 * (policies and start date), so they leave the candidate list.
 */
export function latestOfferByApplicationId(offers: JobOfferLetter[]): Map<string, JobOfferLetter> {
  const sorted = offers
    .filter((offer) => offer.status !== 'voided')
    .slice()
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const byApplication = new Map<string, JobOfferLetter>();
  for (const offer of sorted) {
    if (!byApplication.has(offer.applicationId)) byApplication.set(offer.applicationId, offer);
  }
  return byApplication;
}

export function candidateFinishedHiring(input: {
  application: JobApplication;
  offer: JobOfferLetter | null;
  requiredPolicyCount: number;
  now?: Date;
}): boolean {
  if (!employmentAgreementIsSigned(input.offer)) return false;
  const steps = deriveCandidatePipeline(input);
  return steps.length > 0 && steps.every((step) => step.state === 'complete');
}

export function employmentAgreementIsSigned(offer: Pick<JobOfferLetter, 'status' | 'agreementEmployeeSignature'> | null | undefined): boolean {
  if (!offer) return false;
  return offer.status === 'signed' || Boolean(offer.agreementEmployeeSignature?.dataUrl);
}

/** New sends store null until staff releases the contract. Missing field = legacy sent offers. */
export function employmentAgreementIsReleased(offer: Pick<JobOfferLetter, 'status' | 'contractSentAt'> | null | undefined): boolean {
  if (!offer) return false;
  if (offer.status === 'signed') return true;
  if (offer.contractSentAt instanceof Date) return true;
  if (offer.contractSentAt === null) return false;
  return offer.status === 'sent';
}

export function publicOfferUrl(slug: string): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/offer/${slug}`;
  }
  return `https://clients.lctimberframes.com/offer/${slug}`;
}

export function deriveCandidatePipeline(input: {
  application: JobApplication;
  offer: JobOfferLetter | null;
  requiredPolicyCount: number;
  now?: Date;
}): CandidatePipelineStep[] {
  const { application, offer, requiredPolicyCount } = input;
  const now = input.now || new Date();
  const status = application.status;
  const rejected = status === 'rejected';
  const isNew = status === 'new';
  const isReviewed = status === 'reviewing' || status === 'interview';
  const pastDecision = status === 'offered' || status === 'hired';
  const letterSigned = offerLetterIsSigned(offer);
  const agreementSigned = employmentAgreementIsSigned(offer);
  const offerOut = offer?.status === 'sent' || offer?.status === 'signed';
  const letterInPlay = pastDecision && (offerOut || letterSigned || status === 'hired');
  const acks = offer?.policyAcks?.length || 0;
  const onboardingDone = agreementSigned && acks >= requiredPolicyCount;
  const startIso = offer?.fields.startDate || '';
  const startDate = startIso ? new Date(`${startIso}T12:00:00`) : null;
  const startConfirmed = Boolean(offer?.startDateConfirmedAt);
  const startArrived = Boolean(startDate && startDate.getTime() <= now.getTime());
  const startDone = agreementSigned && Boolean(startIso) && (startConfirmed || startArrived);

  const afterDecisionLocked = rejected;

  const step = (
    id: CandidatePipelineStep['id'],
    label: string,
    complete: boolean,
    currentWhen: boolean,
  ): CandidatePipelineStep => {
    if (id !== 'applied' && id !== 'reviewed' && id !== 'decision' && afterDecisionLocked) {
      return { id, label, state: 'locked' };
    }
    if (complete) return { id, label, state: 'complete' };
    if (currentWhen) return { id, label, state: 'current' };
    return { id, label, state: 'upcoming' };
  };

  const decisionState: CandidatePipelineStep['state'] = rejected
    ? 'rejected'
    : !pastDecision
      ? 'upcoming'
      : letterInPlay
        ? 'complete'
        : 'current';

  return [
    { id: 'applied', label: 'Applied', state: isNew ? 'current' : 'complete' },
    step('reviewed', 'Reviewed', pastDecision || rejected, isReviewed),
    {
      id: 'decision',
      label: rejected ? 'Rejected' : 'Offered',
      state: decisionState,
    },
    step('offerLetter', 'Offer signed', letterSigned, letterInPlay && !letterSigned),
    step('agreement', 'Agreement signed', agreementSigned, letterSigned && employmentAgreementIsReleased(offer) && !agreementSigned),
    step('onboarding', 'Onboarding', onboardingDone, agreementSigned && !onboardingDone),
    step('startDate', 'Start date', startDone, onboardingDone && !startDone),
  ];
}

export const CANDIDATE_TRAIL_LABELS: Record<CandidatePipelineStepId, string> = {
  applied: 'Applied',
  reviewed: 'Reviewed',
  decision: 'Offered',
  offerLetter: 'Letter',
  agreement: 'Contract',
  onboarding: 'Policies',
  startDate: 'Start',
};

export type CandidateStatusSelectValue = CandidatePipelineStepId | 'rejected';

export const CANDIDATE_STATUS_SELECT_OPTIONS: Array<{
  value: CandidateStatusSelectValue;
  label: string;
  staffSettable: boolean;
}> = [
  { value: 'applied', label: 'Applied', staffSettable: true },
  { value: 'reviewed', label: 'Reviewed', staffSettable: true },
  { value: 'decision', label: 'Offered', staffSettable: true },
  { value: 'offerLetter', label: 'Letter', staffSettable: false },
  { value: 'agreement', label: 'Contract', staffSettable: false },
  { value: 'onboarding', label: 'Policies', staffSettable: false },
  { value: 'startDate', label: 'Start', staffSettable: false },
  { value: 'rejected', label: 'Rejected', staffSettable: true },
];

export function applicationStatusTrailLabel(status: JobApplicationStatus): string {
  if (status === 'new') return CANDIDATE_TRAIL_LABELS.applied;
  if (status === 'reviewing' || status === 'interview') return CANDIDATE_TRAIL_LABELS.reviewed;
  if (status === 'offered') return CANDIDATE_TRAIL_LABELS.decision;
  if (status === 'hired') return CANDIDATE_TRAIL_LABELS.agreement;
  return 'Rejected';
}

export function candidateStatusSelectValue(
  status: JobApplicationStatus,
  steps: CandidatePipelineStep[],
): CandidateStatusSelectValue {
  if (status === 'rejected') return 'rejected';
  if (status === 'new') return 'applied';
  if (status === 'reviewing' || status === 'interview') return 'reviewed';
  if (status === 'offered') return 'decision';
  const start = steps.find(s => s.id === 'startDate');
  const onboarding = steps.find(s => s.id === 'onboarding');
  const agreement = steps.find(s => s.id === 'agreement');
  const letter = steps.find(s => s.id === 'offerLetter');
  if (start?.state === 'complete' || start?.state === 'current') return 'startDate';
  if (onboarding?.state === 'complete' || onboarding?.state === 'current') return 'onboarding';
  if (agreement?.state === 'complete' || agreement?.state === 'current') return 'agreement';
  if (letter?.state === 'complete' || letter?.state === 'current') return 'offerLetter';
  return 'offerLetter';
}

export function candidateTrailBadgeLabel(
  status: JobApplicationStatus,
  steps: CandidatePipelineStep[],
): string {
  const value = candidateStatusSelectValue(status, steps);
  return value === 'rejected' ? 'Rejected' : CANDIDATE_TRAIL_LABELS[value];
}

export function applicationStatusForTrailSelect(value: CandidateStatusSelectValue): JobApplicationStatus | null {
  if (value === 'applied') return 'new';
  if (value === 'reviewed') return 'reviewing';
  if (value === 'decision') return 'offered';
  if (value === 'rejected') return 'rejected';
  return null;
}
